import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import evosLogo from '../../../assets/EvosLogo.svg';
import { custonSooner } from '../../../components/CustopmSooner';
import { ProductImage } from '../../../components/ProductImage';
import { cn } from '../../../lib/utils';
import { AppDispatch } from '../../../store';
import { selectAppliedPromoCode } from '../../../store/selectors/basket';
import {
  applyPromoCode,
  resetPromo,
} from '../../../store/slices/basketSlice';
import { impactOccuer } from '../../../utils/hapticEvents';
import { formatPromoValidUntil } from '../libs/formatPromoDate';
import { PromotionPromocode } from '../types/Deals';
import { PromocodeQrDrawer } from './PromocodeQrDrawer';

type PromocodeCardProps = {
  promo: PromotionPromocode;
  /** Без кнопки «Показать QR» — для карточки внутри самой QR-шторки. */
  hideQrButton?: boolean;
};

/**
 * Карточка промокода (макет 38-4042): слева текст и кнопки, справа фото,
 * между ними «перфорация» купона. «Применить» не проверяет код сам — кладёт
 * его в стор, корзина отправит его в calculate-cart.
 */
export const PromocodeCard = ({
  promo,
  hideQrButton = false,
}: PromocodeCardProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const appliedPromoCode = useSelector(selectAppliedPromoCode);
  const [openQr, setOpenQr] = useState(false);

  const code = promo.code || '';
  const qrValue = promo.qrPayload || code;
  const isApplied = Boolean(code) && appliedPromoCode === code;
  const validUntil = formatPromoValidUntil(promo.endDate, i18n.language);

  const handleToggle = () => {
    impactOccuer('light');

    if (isApplied) {
      dispatch(resetPromo());
      custonSooner.success(t('promo-request-cancelled'));
      return;
    }

    dispatch(applyPromoCode(code));
    custonSooner.success(t('promo-requested'));
  };

  return (
    <>
      <div className="flex w-full flex-row items-stretch">
        <div className="flex min-h-[140px] min-w-0 flex-1 flex-col justify-between space-y-1 rounded-2xl bg-fill-base p-3">
          <div className="flex flex-col space-y-1">
            <p className="text-caption-xs-medium text-text-tertiary">
              {t('promocode')}
            </p>
            <p className="break-words font-inter-tight text-[14px] font-semibold leading-[17px] text-text-primary">
              {promo.title}
            </p>
            {validUntil && (
              <p className="text-caption-xs-medium text-text-primitive-yellow">
                {t('promo-valid-until', { date: validUntil })}
              </p>
            )}
          </div>
          <div className="flex flex-row flex-wrap items-center gap-1">
            {code && (
              <button
                type="button"
                className={cn(
                  'h-6 rounded-full px-2 font-inter text-[10px] font-medium leading-5 text-text-white',
                  isApplied ? 'bg-fill-danger' : 'bg-brand-green'
                )}
                onClick={handleToggle}
              >
                {isApplied ? t('cancel') : t('apply')}
              </button>
            )}
            {!hideQrButton && qrValue && (
              <button
                type="button"
                className="h-6 rounded-full bg-fill-background px-2 font-inter text-[10px] font-medium leading-5 text-text-primary"
                onClick={() => {
                  impactOccuer('light');
                  setOpenQr(true);
                }}
              >
                {t('show-qr-short')}
              </button>
            )}
          </div>
        </div>

        {/* Перфорация купона: просвет 2px, перекрытый двумя белыми полосками. */}
        <div className="flex w-[2px] shrink-0 flex-col space-y-8 pt-[22px]">
          <div className="h-8 bg-fill-base" />
          <div className="h-8 bg-fill-base" />
        </div>

        <div className="w-[140px] shrink-0 overflow-hidden rounded-2xl bg-fill-base">
          <ProductImage
            src={promo.imageUrl || promo.img}
            alt="promo"
            fallbackSrc={evosLogo}
            placeholderClassName="p-6"
            className="h-full min-h-[140px] w-full object-cover"
          />
        </div>
      </div>

      {!hideQrButton && qrValue && (
        <PromocodeQrDrawer
          open={openQr}
          setOpen={setOpenQr}
          promo={promo}
          qrValue={qrValue}
        />
      )}
    </>
  );
};
